import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, HardHat, Phone, Smartphone, Users } from 'lucide-react';
import { Button, Callout, Card, CardBody, EmptyState, Field, Input, Loading, PhoneInput, useToast } from '../components/ui.jsx';
import BrandMark from '../components/BrandMark.jsx';
import { api } from '../lib/api.js';

const PHONE = '1-855-232-7996';
const PHONE_HREF = 'tel:+18552327996';
const SUPPORT = 'support@3lumpers.com';

/**
 * Where a man lands when his lead texts him the link.
 *
 * He is not signing up cold — his lead already put his name and number on a
 * crew. All he does here is make his own login so he can be paid and rated on
 * his own, and so he can see the work on his phone.
 */
export default function JoinCrew() {
  const { code } = useParams();
  const toast = useToast();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    firstName: '', lastName: '', phone: '', email: '', password: '', zip: '', city: '', state: 'FL',
  });

  useEffect(() => {
    document.title = 'Join your crew — 3Lumpers';
  }, []);

  useEffect(() => {
    let alive = true;
    api
      .get(`/public/crew-invite/${code}`)
      .then((payload) => {
        if (!alive) return;
        setInvite(payload.data);
        const [first, ...rest] = String(payload.data.name || '').split(' ');
        setForm((prev) => ({ ...prev, firstName: first || '', lastName: rest.join(' ') || '', phone: '' }));
      })
      .catch((err) => alive && setError(err.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [code]);

  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) {
      toast.err('We need your first and last name');
      return;
    }
    if (!form.email.includes('@')) {
      toast.err('We need an email address', 'That is how you sign in to the app.');
      return;
    }
    if (form.password.length < 6) {
      toast.err('Your password needs at least 6 characters');
      return;
    }
    if (!form.zip.trim()) {
      toast.err('We need your ZIP code', 'This is how we know which jobs are near you.');
      return;
    }
    setBusy(true);
    try {
      const payload = await api.post(`/public/crew-invite/${code}`, { ...form, code });
      setDone(payload.data);
    } catch (err) {
      toast.err('That did not go through', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="landing">
      <div className="util-bar">
        <div className="util-bar-inner">
          <Link to="/" className="row gap-10">
            <BrandMark size={26} />
            <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 650 }}>3Lumpers</span>
          </Link>
          <div className="grow" />
          <a href={PHONE_HREF} className="row gap-6">
            <Phone size={13} /> {PHONE}
          </a>
        </div>
      </div>

      <div style={{ padding: '32px 18px 64px', maxWidth: 620, margin: '0 auto' }}>
        {loading ? <Loading rows={4} /> : null}

        {!loading && error ? (
          <Card>
            <CardBody>
              <EmptyState icon={HardHat} title="That invite link is not good any more">
                <p className="muted">{error}</p>
                <p className="muted">
                  Call us on <a href={PHONE_HREF}>{PHONE}</a> or email{' '}
                  <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a> and we will put you back on the crew.
                </p>
              </EmptyState>
            </CardBody>
          </Card>
        ) : null}

        {!loading && !error && done ? (
          <Card>
            <CardBody>
              <div className="col gap-14">
                <span className="row gap-10">
                  <CheckCircle2 size={22} style={{ color: 'var(--green)' }} />
                  <span style={{ fontSize: 'var(--fs-lg)', fontWeight: 680 }}>You are on the crew</span>
                </span>
                <p className="muted" style={{ fontSize: 'var(--fs-md)' }}>
                  {done.leadName ? `${done.leadName} will see you on his crew list.` : 'Your lead will see you on his crew list.'}{' '}
                  Your worker number is <strong>{done.code}</strong>.
                </p>
                <Callout tone="amber" icon={Smartphone} title="Do this next — it takes a minute">
                  <p>
                    Download the 3Lumpers app for your phone, then sign in with <strong>{form.email}</strong> and the
                    password you just made. The work your lead takes will show up on your phone.
                  </p>
                </Callout>
                <p className="dim" style={{ fontSize: 'var(--fs-sm)' }}>
                  No app yet? You can still work. Your lead takes the job and puts your name on it — you do not need the
                  app to get paid. Get it when you want the jobs to come to your phone.
                </p>
                <div className="row gap-10 wrap">
                  <Link to="/signup?path=lumper">
                    <Button variant="outline" icon={ArrowRight}>See what else we do</Button>
                  </Link>
                  <a href={PHONE_HREF}>
                    <Button variant="ghost" icon={Phone}>Call {PHONE}</Button>
                  </a>
                </div>
              </div>
            </CardBody>
          </Card>
        ) : null}

        {!loading && !error && !done ? (
          <>
            <div className="col gap-8 mb-20" style={{ textAlign: 'center' }}>
              <span className="row gap-10" style={{ justifyContent: 'center' }}>
                <Users size={22} style={{ color: 'var(--amber)' }} />
                <span style={{ fontSize: 'var(--fs-2xl)', fontWeight: 700 }}>
                  {invite?.leadName ? `${invite.leadName} put you on his crew` : 'You are joining a crew'}
                </span>
              </span>
              <p className="muted" style={{ fontSize: 'var(--fs-lg)' }}>
                {invite?.crewName ? `${invite.crewName} at 3Lumpers.` : 'Welcome to 3Lumpers.'} Make your own login so
                your work, your pay and your rating are yours — not your lead&rsquo;s.
              </p>
            </div>

            <Card>
              <CardBody>
                <form className="col gap-16" onSubmit={submit}>
                  <div className="grid grid-2" style={{ gap: 14 }}>
                    <Field label="First name">
                      <Input value={form.firstName} onChange={set('firstName')} placeholder="Marcus" autoComplete="given-name" />
                    </Field>
                    <Field label="Last name">
                      <Input value={form.lastName} onChange={set('lastName')} placeholder="Bell" autoComplete="family-name" />
                    </Field>
                  </div>
                  <Field label="Email" hint="You use this to sign in to the app.">
                    <Input type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" autoComplete="email" />
                  </Field>
                  <div className="grid grid-2" style={{ gap: 14 }}>
                    <Field label="Password">
                      <Input type="password" value={form.password} onChange={set('password')} placeholder="At least 6 characters" autoComplete="new-password" />
                    </Field>
                    <Field label="Phone number">
                      <PhoneInput value={form.phone} onChange={set('phone')} placeholder="(305) 123-4567" autoComplete="tel" />
                    </Field>
                  </div>
                  <Field label="ZIP code" hint="This is how we know which jobs are near you.">
                    <Input value={form.zip} onChange={set('zip')} placeholder="33069" inputMode="numeric" autoComplete="postal-code" />
                  </Field>
                  <Field label="City (optional)">
                    <Input value={form.city} onChange={set('city')} placeholder="Pompano Beach" />
                  </Field>
                  <Button type="submit" variant="primary" size="lg" loading={busy} className="btn-block">
                    Join the crew
                  </Button>
                  <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                    Already have a 3Lumpers login? Sign in to the app instead, then tap <strong>Join a crew</strong> and
                    type the code your lead sent you.
                  </p>
                </form>
              </CardBody>
            </Card>
          </>
        ) : null}
      </div>
    </div>
  );
}
